/**
 * @fileoverview Result Detector for integration tests
 *
 * Detects game endings only (win/loss/draw).
 * Status effects, bonus turns, and captures are tracked via the delta output.
 */

import { GameState, State } from "../zodiacEngine/types";
import { TestResult, GameEndType, ParsedMove } from "./types";

/**
 * Detect game ending from game state
 *
 * @param before - Game state before the move
 * @param after - Game state after the move
 * @param move - The move that was executed (unused, kept for API compatibility)
 * @returns TestResult with gameEnd: false or a game ending type
 */
export function detectResult(before: GameState, after: GameState, move: ParsedMove): TestResult {
  // Check victory conditions
  const victoryResult = detectVictory(after);
  if (victoryResult) {
    return victoryResult;
  }

  // Check draw conditions
  const drawResult = detectDraw(after);
  if (drawResult) {
    return drawResult;
  }

  // No game ending
  return { gameEnd: false };
}

/**
 * Detect victory conditions
 */
function detectVictory(state: GameState): TestResult | null {
  // Check for game end
  if (state.state !== State.PostGame && state.state !== State.Draw) {
    return null;
  }

  // Check timeout (if win condition is set)
  if ((state as any).winCondition === "timeout") {
    const winner = state.lightWon ? "light" : "dark";
    return {
      gameEnd: winner === "light" ? "victory_timeout_light" : "victory_timeout_dark",
      winner,
    };
  }

  // Check if we have a winner
  if (state.lightWon !== undefined) {
    const winner = state.lightWon ? "light" : "dark";

    // Determine victory type
    const victoryType = detectVictoryType(state, winner);
    return {
      gameEnd: victoryType,
      winner,
    };
  }

  return null;
}

/**
 * Determine if victory was by race or kingslayer
 */
function detectVictoryType(state: GameState, winner: "light" | "dark"): GameEndType {
  // Check for zombie race victory first
  if ((state as any).winCondition === 'zombie-race') {
    return winner === "light" ? "victory_zombierace_light" : "victory_zombierace_dark";
  }

  // Find kings on the board
  let lightKingFound = false;
  let darkKingFound = false;
  let lightKingPosition: { row: number; col: number } | null = null;
  let darkKingPosition: { row: number; col: number } | null = null;

  for (let row = 0; row < state.board.length; row++) {
    for (let col = 0; col < state.board[row].length; col++) {
      for (const piece of state.board[row][col].Pieces) {
        if (piece.isKing) {
          if (piece.isLight) {
            lightKingFound = true;
            lightKingPosition = { row, col };
          } else {
            darkKingFound = true;
            darkKingPosition = { row, col };
          }
        }
      }
    }
  }

  // If a king is missing, it was captured (kingslayer)
  if (winner === "light" && !darkKingFound) {
    return "victory_kingslayer_light";
  }
  if (winner === "dark" && !lightKingFound) {
    return "victory_kingslayer_dark";
  }

  // Check for race victory
  // Light wins race by reaching row 7, Dark wins race by reaching row 0
  if (winner === "light" && lightKingPosition && lightKingPosition.row === 7) {
    return "victory_race_light";
  }
  if (winner === "dark" && darkKingPosition && darkKingPosition.row === 0) {
    return "victory_race_dark";
  }

  // Default to kingslayer if we can't determine
  return winner === "light" ? "victory_kingslayer_light" : "victory_kingslayer_dark";
}

/**
 * Detect draw conditions
 */
function detectDraw(state: GameState): TestResult | null {
  if (state.state !== State.Draw) {
    return null;
  }

  // Check for repetition (3-fold)
  // Check both old field (repeatString) and new field (drawReason)
  const drawReason = (state as any).drawReason;
  const repeatString = (state as any).repeatString;

  if (
    (repeatString && repeatString.includes("3x")) ||
    (drawReason && drawReason.includes("repeat"))
  ) {
    return { gameEnd: "draw_repetition" };
  }

  // Otherwise it's stalemate
  return { gameEnd: "draw_stalemate" };
}

/**
 * Format a test result for display
 */
export function formatTestResult(result: TestResult): string {
  if (result.gameEnd === false) {
    return "No game ending";
  }

  switch (result.gameEnd) {
    case "victory_race_light":
      return "Light wins by race";
    case "victory_race_dark":
      return "Dark wins by race";
    case "victory_zombierace_light":
      return "Light wins by zombie race";
    case "victory_zombierace_dark":
      return "Dark wins by zombie race";
    case "victory_kingslayer_light":
      return "Light wins by kingslayer";
    case "victory_kingslayer_dark":
      return "Dark wins by kingslayer";
    case "victory_timeout_light":
      return "Light wins by timeout";
    case "victory_timeout_dark":
      return "Dark wins by timeout";
    case "draw_stalemate":
      return "Draw by stalemate";
    case "draw_repetition":
      return "Draw by 3-fold repetition";
    default:
      return `Unknown: ${result.gameEnd}`;
  }
}
